import React, { createContext, useState, useCallback, useMemo, useContext } from 'react';
import { userService } from '../services/UserService';
import { roleService } from '../services/roleService';

export const UserContext = createContext();

export const UserProvider = props => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(0);
    const [total, setTotal] = useState(0);
    const [sortState, setSortState] = useState([{ fieldName: 'Email', dir: 'asc' }]);

    const createUser = useCallback(
        (notes, firstName, lastName, emailAddress, phoneNumber, userRole, password, distributorId) => {
            return userService.postUser(
                notes,
                firstName,
                lastName,
                emailAddress,
                phoneNumber,
                userRole,
                password,
                distributorId
            );
        },
        []
    );

    const updateUser = useCallback(
        (userId, notes, firstName, lastName, emailAddress, phoneNumber, userRole, distributorId) => {
            return userService.editUser(
                userId,
                notes,
                firstName,
                lastName,
                emailAddress,
                phoneNumber,
                userRole,
                distributorId
            );
        },
        []
    );

    const toggleUserActive = useCallback((userId, inactive) => {
        return userService.toggleUserActive(userId, inactive);
    }, []);

    const getRoles = useCallback(() => {
        roleService.getRoles().then(result => {
            setRoles(
                result.data
                    .map(entry => {
                        return { key: entry.id, text: entry.name, value: entry.id };
                    })
                    .reverse()
            );
        });
    }, []);

    const queryUsers = useCallback(async () => {
        const res = await userService.getUsers(pageSize, pageIndex, sortState[0]?.fieldName, sortState[0]?.dir)
        setUsers(res.data);
        setTotal(res.total);
    }, [pageIndex, pageSize, sortState]);

    const deleteUser = useCallback((userId) => {
        return userService.deleteUser(userId);
    }, []);

    const changePassword =  useCallback(async (currentPassword, newPassword, newPasswordConfirm) => {
        return await userService.changePassword(currentPassword, newPassword, newPasswordConfirm);
    }, []);

    const forgotPassword = useCallback(async (username) => {
        return await userService.forgotPassword(username);
    }, []);

    const forgotPasswordChange = useCallback(async (resetToken, userId, password, passwordConfirm) => {
        return await userService.forgotPasswordChange(resetToken, userId, password, passwordConfirm);
    }, []);

    const values = useMemo(
        () => ({
            users,
            total,
            pageSize,
            pageIndex,
            sortState,
            roles,
            setTotal,
            setPageSize,
            setPageIndex,
            setSortState,
            queryUsers,
            createUser,
            updateUser,
            toggleUserActive,
            deleteUser,
            getRoles,
            changePassword,
            forgotPassword,
            forgotPasswordChange
        }),
        [
            users,
            pageSize,
            pageIndex,
            sortState,
            roles,
            total,
            queryUsers,
            createUser,
            updateUser,
            toggleUserActive,
            deleteUser,
            getRoles,
            changePassword,
            forgotPassword,
            forgotPasswordChange
        ]
    );

    return <UserContext.Provider value={values}>{props.children}</UserContext.Provider>;
};

export const useUsers = () => {
    const context = useContext(UserContext);

    if (context === undefined) {
        throw new Error('useUsers must be used within a UserProvider component.');
    }

    return context;
};
